export type AuditScoreBand = "excellent" | "good" | "fair" | "poor" | "unknown";

export function getAuditScoreBand(score: number | null | undefined): AuditScoreBand {
  if (typeof score !== "number") return "unknown";
  if (score >= 80) return "excellent";
  if (score >= 60) return "good";
  if (score >= 40) return "fair";
  return "poor";
}

// NOTE: the bands are coloured through two parallel token systems.
// `getAuditScoreTextClass` uses the semantic Tailwind classes
// (`text-warning`/`text-destructive`); `getAuditScoreColorVar` uses the Wave A
// severity palette (`--color-sev-high`/`--color-sev-critical`). They resolve
// identically today only because the underlying values are duplicated —
// `--warning` (a7y-webapp/src/index.css:158) and `--color-sev-high` (:79) are
// both `oklch(0.7 0.19 50)`. Nothing keeps them in step; a change to one will
// silently diverge the other. Out of scope for A7Y-2802.
export function getAuditScoreColorVar(
  score: number | null | undefined,
  fallback = "var(--color-border)",
): string {
  switch (getAuditScoreBand(score)) {
    case "excellent":
      return "var(--color-success)";
    case "good":
      return "var(--color-brand-green)";
    case "fair":
      return "var(--color-sev-high)";
    case "poor":
      return "var(--color-sev-critical)";
    default:
      return fallback;
  }
}

export function getAuditScoreTextClass(
  score: number | null | undefined,
  fallback = "text-[var(--color-muted-foreground)]",
): string {
  switch (getAuditScoreBand(score)) {
    case "excellent":
      return "text-success";
    case "good":
      return "text-brand-green";
    case "fair":
      return "text-warning";
    case "poor":
      return "text-destructive";
    default:
      return fallback;
  }
}

/**
 * Rendered health vocabulary for a score-derived signal.
 *
 * `"unknown"` is the absent-score state and is deliberately *neutral*, not
 * positive: it means "no score has been produced", which is a different claim
 * from "this dimension is fine". Every other helper in this file already keeps
 * that distinction — `getAuditScoreBand` returns the `"unknown"` band,
 * `getAuditScoreBandLabel` returns "Not yet scored", `getAuditScoreColorVar`
 * falls back to a neutral border. A7Y-2802 brings the health helper into line.
 */
export type AuditHealth = "red" | "yellow" | "green" | "unknown";

export function getAuditScoreHealth(
  score: number | null | undefined,
  fallback: AuditHealth = "unknown",
): AuditHealth {
  switch (getAuditScoreBand(score)) {
    case "excellent":
    case "good":
      return "green";
    case "fair":
      return "yellow";
    case "poor":
      return "red";
    default:
      return fallback;
  }
}

/**
 * Human-readable band label keyed off the same thresholds as
 * `getAuditScoreBand`. Returns "Not yet scored" when the score is
 * null/undefined so hero surfaces can render a neutral placeholder before the
 * engine has produced a score for the current market.
 */
export function getAuditScoreBandLabel(
  score: number | null | undefined,
): string {
  switch (getAuditScoreBand(score)) {
    case "excellent":
      return "Excellent";
    case "good":
      return "Good";
    case "fair":
      return "Fair";
    case "poor":
      return "Poor";
    default:
      return "Not yet scored";
  }
}
